import React, {useEffect, useState, useRef} from "react";
import JoditEditor from "jodit-react";
import {Link, useParams, useNavigate} from "react-router-dom";
import {useForm} from "react-hook-form";
import {toast} from "react-toastify";
import articleService from "../../../services/articles.service";
import Swal from '../../helpers/swal';
import CategoryTreeSelect from "../CategoryTreeSelect";


export default function EditArticle() {
    const [content, setContent] = useState("");
    const [category, setCategory] = useState([]);
    const [article, setArticle] = useState(null);
    const {articleId} = useParams();
    const editor = useRef(null);
    const navigate = useNavigate();

    const config = {
        readonly: false,
        height: "80vh",
        disablePlugins: ["powered-by-jodit", "file"],
    };

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: {errors, isSubmitting},
    } = useForm();

    useEffect(() => {
        // Fetch category list
        articleService.getArticlesCategory()
            .then((res) => setCategory(res.data))
            .catch(console.error);

        // Fetch article data
        articleService.getArticleById(articleId)
            .then((res) => {
                setArticle(res.data);
                setContent(res.data.content);
                setValue("id", res.data.id);
                setValue("title", res.data.title);
                setValue("summary", res.data.summary);
                setValue("status", res.data.status.toString());
                setValue("articleCategoryId", res.data.articleCategoryId);
            })
            .catch(console.error);
    }, [articleId, setValue]);

    const onSubmit = async (data) => {
        try {
            const newData = {...data, content};
            await articleService.saveArticles(newData);
            toast.success("Article saved successfully!", {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
            setTimeout(() => navigate("/articles"), 2000);
        } catch (err) {
            toast.error("Error saving article", {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
            console.error(err);
        }
    };

    const handleDelete = async () => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `
            <i class="bi bi-check"></i>
            Yes, delete it!`,
            cancelButtonText: `
            <i class="bi bi-x"></i> 
            Cancel`,
            reverseButtons: true,
            focusCancel: true,
            customClass: {
              confirmButton: `btn btn-success btn-sm me-2`,
              cancelButton: 'btn btn-secondary btn-sm',
              title: `card-title`,
              html: `small`
            }
        });

        if (result.isConfirmed) {
            try {
                await articleService.deleteArticles(articleId);

                await Swal.fire({
                    title: 'Deleted!',
                    text: 'Your article has been deleted.',
                    icon: 'success',
                    timer: 2000,
                    timerProgressBar: true,
                    willClose: () => navigate('/articles')
                });

            } catch (err) {
                Swal.fire({
                    title: 'Error!',
                    text: 'Failed to delete article',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
                console.error(err);
            }
        }
    };

    return (
        <div className="container-fluid px-4">
            {/* Header Section */}
            <div className="container container-xxl">
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <i className="bi bi-file-earmark-text fs-3 text-primary me-3"></i>
                        <h1 className="h4 mb-0">Edit Article</h1>
                    </div>
                    <Link to="/articles" className="btn btn-outline-secondary btn-sm">
                        <i className="bi bi-chevron-left me-1"></i> Back to Articles
                    </Link>
                </div>
            </div>

            {/* Main Content */}
            <main className="container container-xxl mt-4">
                <div className="row g-4">
                    {/* Editor Column */}
                    <div className="col-lg-8">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-body p-0">
                                <JoditEditor
                                    ref={editor}
                                    value={content}
                                    onChange={(newContent) => {
                                    }}
                                    onBlur={setContent}
                                    config={config}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Sidebar Form */}
                    <div className="col-lg-4">
                        <div className="card border-0 shadow-sm sticky-top" style={{top: "1rem"}}>
                            <div className="card-header bg-white border-bottom">
                                <h5 className="mb-0 fw-semibold">
                                    Article Details
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="p-2"></div>
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    {/* Title Field */}
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">
                                            Title <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            {...register("title", {required: true})}
                                            className={`form-control ${errors.title ? "is-invalid" : ""}`}
                                            placeholder="Enter article title"
                                        />
                                        {errors.title && (
                                            <div className="invalid-feedback">Title is required</div>
                                        )}
                                    </div>

                                    {/* Status Field */}
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Status</label>
                                        <select
                                            {...register("status")}
                                            className="form-select"
                                        >
                                            <option value="true">Active</option>
                                            <option value="false">Inactive</option>
                                        </select>
                                    </div>

                                    {/* Category Field */}
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold mb-2">Category</label>
                                        <input
                                            type="hidden"
                                            {...register("articleCategoryId", {required: true})}
                                        />
                                        <CategoryTreeSelect
                                            categories={category}
                                            value={watch("articleCategoryId")}
                                            onChange={(id) => setValue("articleCategoryId", id, {shouldValidate: true})}
                                        />
                                        {errors.articleCategoryId && (
                                            <div className="text-danger small mt-1">
                                                Please select a category (choose through all subcategory levels)
                                            </div>
                                        )}
                                    </div>

                                    {/* Summary Field */}
                                    <div className="mb-4">
                                        <label className="form-label fw-semibold">Summary</label>
                                        <textarea
                                            className="form-control"
                                            {...register("summary")}
                                            rows="4"
                                            placeholder="Brief article summary..."
                                        ></textarea>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="d-flex gap-2">
                                        <button
                                            type="submit"
                                            className="btn btn-success flex-grow-1"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                                    Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-save me-2"></i>Save Article
                                                </>
                                            )}
                                        </button>

                                        <button
                                            type="button"
                                            className="btn btn-danger"
                                            onClick={handleDelete}
                                        >
                                            <i className="bi bi-trash me-2"></i>Delete
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}